import {
  e1rmConfidenceFromEffort,
  type E1rmConfidence,
} from "./e1rmConfidence.js";
import { effectiveRir } from "./effortScale.js";
import { computeE1rmKg, e1rmAtEffort } from "./pr.js";
import type { LogSetKind } from "./schemas/logSet.js";
import type { TimedSessionResult } from "./progressTypes.js";

/** A point's confidence, minus `no-estimate`: a point only exists here once
 *  `hasE1rm` has passed, so "there is no estimate" is not a reachable verdict. */
export type E1rmPointConfidence = Exclude<E1rmConfidence, "no-estimate">;

// THE funnel every e1RM time-series goes through (CN-26).
//
// "Walk the session results, keep the exercise rows that carry a usable
// e1RM, and turn each one into a dated point" was written out by hand in
// four places: the tier-one champion chart, the regression alerts panel,
// and twice inside the tier-three narrative (once to score the top lift,
// once to build its series). Four copies of one definition of what counts
// as an observation.
//
// They agreed today. The reason to collapse them BEFORE anything else is
// that they were about to stop agreeing: the measurement contract adds a
// confidence bit to every e1RM point (e1rmConfidence.ts — what the number
// PROVES, not just what it is), and threading that through four hand-rolled
// loops is a guarantee that three of them end up with a slightly different
// idea of which points qualify. One funnel, one place to widen.
//
// Lives in its own module rather than in pr.ts: that file is at its line
// budget, and this is a different concern anyway. pr.ts owns the formula
// (what an e1RM IS); this owns the series (which observations there ARE).

/**
 * One dated e1RM observation.
 *
 * `kgPerRep1` rather than `kg`: the value is a one-rep-equivalent estimate,
 * not a weight anyone lifted, and the name is what stops the two from being
 * mixed up downstream.
 *
 * `rir` is `null` when the set that produced the point carried no effort tag.
 * ⛔ Null is NOT zero and NOT "easy" — it is an untagged set, i.e. one we know
 * nothing about. Over three years of history that is the majority of them.
 * Any consumer that reads this field has to say what it does with null
 * explicitly, or it will state something false with a confident tone.
 */
export type E1rmPoint = {
  atMs: number;
  kgPerRep1: number;
  rir: number | null;
  /**
   * What this point PROVES, graded once here so no downstream consumer
   * re-derives it (the four-way drift this funnel exists to kill). A floor the
   * athlete actually stood on (`proven`) and a number from an untagged set
   * (`unknown`) are different data even at the same kg — measurement-contract
   * §3's three states start from this bit. ⛔ `unknown` is a THIRD state, never
   * "easy": an untagged set is one we know nothing about.
   *
   * Read-time only, from the resolved `topSetRir` the aggregate already keeps.
   * `amrap` is not on the aggregate, but the AMRAP meter variant (measurement-contract.md, 2026-07-23) commits new AMRAP sets to RIR 0,
   * so their `rir===0` grades `proven` here anyway; a legacy AMRAP with a stale
   * non-zero RIR is the one case this under-classifies (documented, not stamped
   * — the full-fidelity path is a derived-table backfill).
   */
  confidence: E1rmPointConfidence;
};

/**
 * The identity of a load observation: an exercise ON a given machine.
 *
 * ⭐ Exported because it has TWO producers that must agree — `computeSessionResult`
 * groups a session's sets by it, and this funnel groups the stored rows by it. If
 * they ever keyed differently the failure would be silent: a plausible number
 * computed from the wrong comparison, which is worse than no number.
 *
 * `undefined` instance = the Default machine. That is what every set logged
 * before EQ-1-HIST carries, so an untagged history keys exactly as it always
 * did and needs no backfill.
 */
export const loadTrackKey = (
  slug: string,
  instance: string | undefined,
): string => (instance === undefined ? slug : `${slug}\u0000${instance}`);

/** One track's series — an exercise on one machine — plus the display name
 *  captured at write time. */
export type E1rmSeries = {
  /** The exercise. Consumers resolve the display name through THIS, never
   *  through the map key, which is an opaque composite. */
  slug: string;
  /** Which machine, when the athlete named one. Absent = the Default. */
  equipmentInstance?: string;
  /**
   * `exerciseName` as stamped on the FIRST result seen for this slug. It is a
   * FALLBACK for a deleted catalog row, never the render source: the live name
   * is resolved through the slug, which is the foreign key.
   */
  snapshot: string | undefined;
  /** Chronological, oldest first. */
  points: E1rmPoint[];
};

/** The window to keep, in epoch ms. Both bounds inclusive; omit for no bound. */
export type E1rmWindow = { fromMs?: number; toMs?: number };

/**
 * The one predicate that decides whether an exercise row carries an e1RM
 * observation at all.
 *
 * `topE1rmKg` is nullable on the stored result and is 0 for a session where
 * nothing qualified (no weighted set, reps outside the Epley window). Both
 * mean "no observation", and both have to be rejected here rather than
 * flowing downstream as a zero — a 0 kg point plotted on a chart or fed to a
 * regression check is a fabricated collapse, not a data point.
 */
const hasE1rm = (topE1rmKg: number | null): topE1rmKg is number =>
  topE1rmKg !== null && topE1rmKg > 0;

/**
 * Build `slug → series` from session results, chronologically sorted.
 *
 * Callers that already hold a filtered array can omit the window; the two are
 * equivalent, and the option exists so a caller with the full history does not
 * have to pre-slice just to call this.
 */
export const e1rmPointsByTrack = (
  results: TimedSessionResult[],
  window?: E1rmWindow,
): Map<string, E1rmSeries> => {
  const fromMs = window?.fromMs ?? Number.NEGATIVE_INFINITY;
  const toMs = window?.toMs ?? Number.POSITIVE_INFINITY;
  const bySlug = new Map<string, E1rmSeries>();

  for (const ts of results) {
    const atMs = ts.startedAtMs;
    if (atMs < fromMs || atMs > toMs) continue;
    for (const er of ts.result.exerciseResults) {
      if (!hasE1rm(er.topE1rmKg)) continue;
      // EQ-1-HIST — one series per (exercise, machine). Two machines' kilos are
      // not comparable, so folding them into one line draws a trend that no
      // single machine ever had.
      const key = loadTrackKey(er.exerciseSlug, er.equipmentInstance);
      const cur = bySlug.get(key) ?? {
        slug: er.exerciseSlug,
        ...(er.equipmentInstance
          ? { equipmentInstance: er.equipmentInstance }
          : {}),
        snapshot: er.exerciseName,
        points: [],
      };
      const rir = er.topSetRir ?? null;
      cur.points.push({
        atMs,
        kgPerRep1: er.topE1rmKg,
        rir,
        // Graded from the resolved rir the aggregate keeps (fail already folded
        // to 0 by effectiveRir upstream). Point existence is settled by hasE1rm
        // above, so `no-estimate` can't occur — the effort tail is all that's
        // left to decide.
        confidence: e1rmConfidenceFromEffort({ rir }),
      });
      bySlug.set(key, cur);
    }
  }

  // Sorted here, once, so no caller has to remember. Results arrive newest-
  // first from the API and oldest-first from a live rebuild, and every
  // consumer downstream (trend slope, regression, chart) assumes chronological
  // — an unsorted series does not throw, it just draws the wrong line.
  for (const series of bySlug.values()) {
    series.points.sort((a, b) => a.atMs - b.atMs);
  }
  return bySlug;
};

/**
 * The same observation, taken from SETS instead of from a stored result.
 *
 * Why the funnel grows a second door rather than a second module: the logger
 * holds draft/history entries, not `SessionResult`s, so it cannot use
 * `e1rmPointsByTrack` at all — and "walk an entry's sets, keep the e1RM-eligible
 * ones, take the best" was already written out a second time in the Logger
 * (`entryHistoryPick.ts`, which additionally forgot `weight > 0`). Two inputs,
 * ONE definition of what an observation is: that is the whole point of CN-26.
 *
 * ⭐ Same pick rule as the map above, deliberately: the MAX e1RM set, graded as
 * it stands. One definition of the session's point, not two.
 *
 * `type` rather than `kind`: this reads `WorkoutSet`, the logger's discriminated
 * union, whose discriminator is spelled differently from `LogSet`'s. Structural
 * on purpose so this module stays free of the workout schema.
 */
/** The logger's `WorkoutSet` seen structurally, so this module stays free of
 *  the workout schema (see the note on the discriminator above). */
export type E1rmCandidateSet = {
  type: string;
  reps?: number;
  weight?: number;
  rir?: number | null;
  rpe?: number | null;
  fail?: boolean;
  amrap?: boolean;
};

/**
 * The winning set itself — not just the estimate it produced.
 *
 * ⭐ Exists because the argmax was about to be written a THIRD time. The rule
 * "keep the eligible sets, take the max, first one wins a tie" is one rule; a
 * caller that needs the raw `weightKg × reps` behind the winner (EQ-2's set
 * proposal does — you cannot put a `kgPerRep1` on a bar) was copying the loop
 * because `E1rmPoint` has nowhere to put them. That is a signature gap, not a
 * reason for a second loop: the gap is closed here instead.
 *
 * ⛔ Any change to the pick rule — prefer more reps on a tie, weight by effort,
 * treat `fail` differently — belongs HERE and nowhere else.
 */
export type BestE1rmSet<S extends E1rmCandidateSet = E1rmCandidateSet> = {
  kgPerRep1: number;
  weightKg: number;
  reps: number;
  rir: number | null;
  amrap: boolean;
  /** The winning set itself, so a caller never searches for it again. */
  set: S;
};

/** `atEffort` (D9, the «today» anchor): the e1RM at the logged effort
 *  (`e1rmAtEffort`), the rule the load chain reads it back with. Off: Epley
 *  on the reps, the e1RM the server stamps. */
export const bestE1rmSet = <S extends E1rmCandidateSet>(
  sets: readonly S[],
  opts: { atEffort?: boolean } = {},
): BestE1rmSet<S> | null => {
  let best: BestE1rmSet<S> | null = null;
  for (const s of sets) {
    const { reps, weight } = s;
    // Narrowed before the gate rather than asserted after it: `computeE1rmKg`
    // returning non-null already implies both are present, but saying so with
    // `!` would be a claim, and this is a fact.
    if (reps === undefined || weight === undefined) continue;
    // One eligibility gate (kind, reps, weight), then the effort's band.
    const plain = computeE1rmKg({ kind: s.type as LogSetKind, reps, weight });
    if (plain === null) continue;
    const rir = effectiveRir(s);
    const kg = opts.atEffort ? e1rmAtEffort(weight, reps, rir) : plain;
    if (kg === null) continue;
    if (best !== null && kg <= best.kgPerRep1) continue;
    best = { kgPerRep1: kg, weightKg: weight, reps, rir, amrap: s.amrap === true, set: s };
  }
  return best;
};

export const e1rmPointFromSets = (
  atMs: number,
  sets: readonly E1rmCandidateSet[],
): E1rmPoint | null => {
  const best = bestE1rmSet(sets);
  if (best === null) return null;
  return {
    atMs,
    kgPerRep1: best.kgPerRep1,
    rir: best.rir,
    confidence: e1rmConfidenceFromEffort({ amrap: best.amrap, rir: best.rir }),
  };
};

/**
 * "Which lift is this athlete's main one", by frequency × best estimate.
 *
 * Reads the same map so the answer can never be computed from a different set
 * of points than the series it then goes on to describe.
 */
/** The map KEY of the best-scoring track, to be fed straight back to
 *  `.get()`. ⛔ Not a slug since EQ-1-HIST — read `.slug` off the series if you
 *  need the exercise. */
export const topE1rmTrack = (bySlug: Map<string, E1rmSeries>): string | null => {
  let best: string | null = null;
  let bestScore = 0;
  for (const [key, series] of bySlug) {
    const maxE1rm = series.points.reduce(
      (acc, p) => Math.max(acc, p.kgPerRep1),
      0,
    );
    const score = series.points.length * maxE1rm;
    if (score > bestScore) {
      bestScore = score;
      best = key;
    }
  }
  return best;
};

/** Best e1RM (kg) for one exercise out of the SERVER-COMPUTED all-time PR
 *  records, or null when nothing in them is valid 1RM evidence.
 *
 *  ⭐ `MX-10` (Antenore 2026-09-07). The timeline card used to compare a set
 *  against the best inside the logger's rolling 50-SESSION window and label the
 *  winner «best» — a superlative measured on a window it never named. His
 *  barbell-row read «best» at e1RM 76 while 84 and 77 sat nine sessions past
 *  the edge. `/api/me/records` is the only source with no window in it: it is
 *  computed over the whole user partition (`apps/api/src/routes/records.ts`),
 *  and the Logger ALREADY fetches and caches it for the PR pill, so reading it
 *  here costs no request at all.
 *
 *  ⛔ Not every bucket is e1RM evidence. `prsFromHistory` fills rep buckets up
 *  to whatever a set actually performed (a 20-rep set fills 1..20), while Epley
 *  is only valid at low reps — so the SAME `computeE1rmKg` funnel every other
 *  e1RM reader goes through decides, and it is the only definition here.
 *
 *  ⛔ Sibling of `bestE1rmSet`, not a variant of it: that one picks the best
 *  SET out of a flat list (and carries the set back); this one reduces stored
 *  RECORDS to a single number. Same funnel, two different inputs. */
export const bestE1rmFromPrRecords = (
  buckets: Record<number, { weightKg: number; reps: number }> | undefined,
): number | null => {
  if (buckets === undefined) return null;
  let best: number | null = null;
  for (const rec of Object.values(buckets)) {
    const kg = computeE1rmKg({
      kind: "weighted" as LogSetKind,
      reps: rec.reps,
      weight: rec.weightKg,
    });
    if (kg === null) continue;
    if (best === null || kg > best) best = kg;
  }
  return best;
};
